// NEW_FILE_JUSTIFIED: New page component for WhatsApp messaging — uses distinct useWhatsAppMessages hook and WhatsApp-specific contact type labels.
import { useReducer, useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useAction, useQuery } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import { CreateLeadDialog } from '@/components/leads/CreateLeadDialog';
import { ConversationList } from '@/components/messages/ConversationList';
import { ChatPanel } from '@/components/messages/ChatPanel';
import { ContactPanel } from '@/components/messages/ContactPanel';
import { IconSidebar } from '@/components/messages/IconSidebar';
import {
  ConversationListSkeleton,
  ChatPanelSkeleton,
  ContactPanelSkeleton,
} from '@/components/messages/MessageSkeletons';
import { useWhatsAppMessages } from '@/hooks/useWhatsAppMessages';
import { MOCK_TAGS } from '@/constants/messagesMockData';
import type { IContactDetail, IReplyContext, IMessage } from '@/@types/messages';
import type { IAILeadExtras, IMatchedService } from '@/@types/leads';
import type { IOrgServiceType } from '@/@types/serviceTypes';
import type { IInsuranceField } from '@/@types/insurance';
import type {
  IMessagesPageState,
  TMessagesPageAction,
  TConversationFilter,
  TRightPanelView,
  TMessageChannel,
} from '@/@types/messages';

// ============================================================================
// Reducer (same shape as MessagesPage)
// ============================================================================

const initialState: IMessagesPageState = {
  selectedConversationId: '',
  activeRightPanel: 'contact',
  filter: 'all',
  searchQuery: '',
  activeChannel: 'whatsapp',
};

function whatsappPageReducer(
  state: IMessagesPageState,
  action: TMessagesPageAction
): IMessagesPageState {
  switch (action.type) {
    case 'SET_CONVERSATION':
      return { ...state, selectedConversationId: action.payload };
    case 'SET_RIGHT_PANEL':
      return { ...state, activeRightPanel: action.payload };
    case 'SET_FILTER':
      return { ...state, filter: action.payload };
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload };
    case 'SET_CHANNEL':
      return { ...state, activeChannel: action.payload };
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

// ============================================================================
// Component
// ============================================================================

export function WhatsAppPage() {
  const [state, dispatch] = useReducer(whatsappPageReducer, initialState);

  const [searchParams] = useSearchParams();
  const contactFromUrl = searchParams.get('contact');
  const nameFromUrl = searchParams.get('name');
  const phoneFromUrl = searchParams.get('phone');

  const {
    conversations,
    messages,
    isLoading,
    markRead,
    sendMessage,
    sendMediaMessage,
    sendReaction,
    deleteMessage,
    contactDetails,
    fetchContactInfo,
  } = useWhatsAppMessages(
    state.selectedConversationId !== '' ? state.selectedConversationId : null
  );

  const currentOrg = useQuery(api.organisations.queries.getCurrentOrg);
  const orgServiceTypes = useQuery(
    api.serviceTypes.listByOrg,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const generateUploadUrl = useMutation(api.storage.createUploadUrl);
  const [isSendingFile, setIsSendingFile] = useState(false);
  const [replyContext, setReplyContext] = useState<IReplyContext | null>(null);
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [createLeadPrefill, setCreateLeadPrefill] = useState({ name: '', email: '', phone: '' });
  const [aiExtras, setAiExtras] = useState<IAILeadExtras | undefined>(undefined);
  const [matchedServices, setMatchedServices] = useState<IMatchedService[] | undefined>(undefined);
  const [unmatchedServices, setUnmatchedServices] = useState<string[] | undefined>(undefined);
  const [isExtracting, setIsExtracting] = useState(false);
  const extractLeadData = useAction(api.leads.aiLeadExtraction.extractLeadData);

  // Build contact detail from the selected conversation (or URL params for new contacts)
  const selectedContact = useMemo((): IContactDetail | null => {
    const conv = conversations.find(c => c.id === state.selectedConversationId);
    if (conv !== undefined) {
      return {
        id: conv.id,
        name: conv.contactName,
        initials: conv.contactInitials,
        avatarColor: conv.avatarColor,
        contactType: conv.chatType === 'group' ? 'Group' : 'WhatsApp',
        contactTypeColor: conv.chatType === 'group' ? '#6366F1' : '#25D366',
        username: conv.contactUsername,
        phone: contactDetails?.phone,
        chatType: conv.chatType,
        assignedTo: '',
        profilePicUrl: contactDetails?.profilePicUrl ?? conv.profilePicUrl,
      };
    }
    // Virtual contact from URL params (no existing conversation)
    if (contactFromUrl) {
      const name = nameFromUrl ?? phoneFromUrl ?? contactFromUrl;
      return {
        id: contactFromUrl,
        name,
        initials: name.slice(0, 2).toUpperCase(),
        avatarColor: '#25D366',
        contactType: 'WhatsApp',
        contactTypeColor: '#25D366',
        phone: phoneFromUrl ?? undefined,
        chatType: 'personal',
        assignedTo: '',
      };
    }
    return null;
  }, [
    conversations,
    state.selectedConversationId,
    contactDetails,
    contactFromUrl,
    nameFromUrl,
    phoneFromUrl,
  ]);

  const filteredConversations = useMemo(() => {
    let result = conversations;

    if (state.filter === 'personal') {
      result = result.filter(c => c.chatType !== 'group');
    } else if (state.filter === 'groups') {
      result = result.filter(c => c.chatType === 'group');
    } else if (state.filter === 'unread') {
      result = result.filter(c => c.unreadCount > 0);
    }

    if (state.searchQuery.trim()) {
      const query = state.searchQuery.toLowerCase();
      result = result.filter(
        c =>
          c.contactName.toLowerCase().includes(query) || c.lastMessage.toLowerCase().includes(query)
      );
    }

    return result;
  }, [conversations, state.filter, state.searchQuery]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      dispatch({ type: 'SET_CONVERSATION', payload: id });
      markRead(id).catch(() => {
        // Mark read is best-effort
      });
    },
    [markRead]
  );

  const handleFilterChange = useCallback((filter: TConversationFilter) => {
    dispatch({ type: 'SET_FILTER', payload: filter });
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH', payload: query });
  }, []);

  const handleRightPanelChange = useCallback((view: TRightPanelView) => {
    dispatch({ type: 'SET_RIGHT_PANEL', payload: view });
  }, []);

  const handleChannelChange = useCallback((channel: TMessageChannel) => {
    dispatch({ type: 'SET_CHANNEL', payload: channel });
  }, []);

  const handleSendMessage = useCallback(
    async (text: string, replyToMessageId?: string | number) => {
      if (state.selectedConversationId === '') return;

      // Use conversation data if available, fall back to selectedContact for new contacts with no messages
      const selectedConv = conversations.find(c => c.id === state.selectedConversationId);
      const contactName = selectedConv?.contactName ?? selectedContact?.name ?? 'Unknown';
      const chatType = selectedConv?.chatType ?? selectedContact?.chatType;

      try {
        await sendMessage({
          whatsappContactId: state.selectedConversationId,
          contactName,
          text,
          chatType,
          // AS_JUSTIFIED: platformMessageId is always string for WhatsApp (whatsappMessageId from schema)
          replyToMessageId: replyToMessageId as string | undefined,
          replyToText: replyContext?.text,
          replyToSenderName: replyContext?.senderName,
        });
        setReplyContext(null); // Clear reply after sending
      } catch {
        toast.error('Failed to send message. Please try again.');
      }
    },
    [state.selectedConversationId, conversations, selectedContact, sendMessage, replyContext]
  );

  const handleReaction = useCallback(
    async (platformMessageId: string | number, emoji: string) => {
      // Need isFromMe — look up from the messages array
      const msg = messages.find(m => m.platformMessageId === platformMessageId);
      // AS_JUSTIFIED: platformMessageId is always string for WhatsApp (whatsappMessageId from schema)
      await sendReaction(platformMessageId as string, emoji, msg?.isFromMe ?? false);
    },
    [sendReaction, messages]
  );

  const handleDeleteMessage = useCallback(
    async (platformMessageId: string | number, forEveryone: boolean) => {
      const msg = messages.find(m => m.platformMessageId === platformMessageId);
      // AS_JUSTIFIED: platformMessageId is always string for WhatsApp (whatsappMessageId from schema)
      await deleteMessage(platformMessageId as string, forEveryone, msg?.isFromMe ?? false);
    },
    [deleteMessage, messages]
  );

  const handleReplyMessage = useCallback((message: IMessage) => {
    setReplyContext({
      messageId: message.platformMessageId ?? message.id,
      text:
        message.text ??
        message.mediaFilename ??
        (message.mediaType ? `[${message.mediaType}]` : undefined),
      senderName: message.senderName,
    });
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyContext(null);
  }, []);

  const handleCreateLead = useCallback(async () => {
    if (!selectedContact) return;

    setIsExtracting(true);
    setAiExtras(undefined);
    setMatchedServices(undefined);
    setUnmatchedServices(undefined);

    try {
      const chatMessages = messages.map(m => ({
        sender: m.isOutgoing ? 'Agent' : selectedContact.name || 'Lead',
        text: m.text,
        timestamp: Date.now(),
        mediaStorageId: m.mediaStorageId,
        mediaType: m.mediaType,
        mediaMimeType: m.mediaMimeType,
        mediaFilename: m.mediaFilename,
      }));

      const serviceTypesForAI = orgServiceTypes?.map((st: IOrgServiceType) => ({
        name: st.name,
        requiredFields: st.requiredFields.map((f: IInsuranceField) => ({
          label: f.label,
          fieldType: f.fieldType,
          description: f.description,
          options: f.options,
        })),
      }));

      const result = await extractLeadData({
        type: 'chat',
        platform: 'whatsapp',
        messages: chatMessages,
        contactName: selectedContact.name,
        contactPhone: selectedContact.phone,
        serviceTypes: serviceTypesForAI,
      });

      setCreateLeadPrefill({
        name: result.name ?? selectedContact.name,
        email: result.email ?? '',
        phone: result.phoneNumber ?? selectedContact.phone ?? '',
      });
      setAiExtras({
        leadType: result.leadType,
        temperature: result.temperature,
        serviceInterest: result.serviceInterest,
        estimatedValue: result.estimatedValue,
        notes: result.notes,
        source: result.source ?? 'whatsapp',
      });
      setMatchedServices(result.matchedServices);
      setUnmatchedServices(result.unmatchedServices);
    } catch (error) {
      console.error('AI lead extraction failed:', error);
      // Graceful degradation
      setCreateLeadPrefill({
        name: selectedContact.name,
        email: '',
        phone: selectedContact.phone ?? '',
      });
      setAiExtras({ source: 'whatsapp' });
    } finally {
      setIsExtracting(false);
      setCreateLeadOpen(true);
    }
  }, [selectedContact, messages, extractLeadData, orgServiceTypes]);

  const handleSendFile = useCallback(
    async (file: File, caption: string) => {
      if (state.selectedConversationId === '') return;

      const selectedConv = conversations.find(c => c.id === state.selectedConversationId);
      if (selectedConv === undefined) return;

      setIsSendingFile(true);
      try {
        const uploadUrl = await generateUploadUrl();
        const uploadResult = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });

        if (!uploadResult.ok) {
          throw new Error('Upload failed');
        }

        const { storageId } = (await uploadResult.json()) as { storageId: string }; // AS_JUSTIFIED: Convex upload API always returns { storageId: string }

        const mediaType = file.type.startsWith('image/')
          ? ('image' as const)
          : ('document' as const);

        await sendMediaMessage({
          contactId: state.selectedConversationId,
          contactName: selectedConv.contactName,
          storageId,
          mediaType,
          filename: file.name,
          mimeType: file.type,
          size: file.size,
          text: caption || undefined,
          chatType: selectedConv.chatType,
        });
      } catch {
        toast.error('Failed to send file. Please try again.');
      } finally {
        setIsSendingFile(false);
      }
    },
    [state.selectedConversationId, conversations, sendMediaMessage, generateUploadUrl]
  );

  // Auto-fetch contact info for personal chats
  useEffect(() => {
    const selectedConv = conversations.find(c => c.id === state.selectedConversationId);
    if (
      state.selectedConversationId !== '' &&
      selectedConv?.chatType !== 'group' &&
      (contactDetails === null || contactDetails.profilePicUrl == null)
    ) {
      // Fetch when we have no contact record yet, OR when the avatar is still
      // missing — pre-existing contacts predate stored avatars, so opening the
      // chat pulls a fresh pic into Convex storage (healing header + list both).
      fetchContactInfo(state.selectedConversationId).catch(() => {
        // Contact info fetch is best-effort
      });
    }
  }, [state.selectedConversationId, contactDetails, fetchContactInfo, conversations]);

  // Auto-select conversation from URL param
  useEffect(() => {
    if (contactFromUrl !== null && contactFromUrl !== '' && state.selectedConversationId === '') {
      dispatch({ type: 'SET_CONVERSATION', payload: contactFromUrl });
    }
  }, [contactFromUrl, state.selectedConversationId]);

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-border shrink-0">
        <h1 className="text-2xl font-bold text-foreground">WhatsApp</h1>
      </div>

      {/* Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Loading state announcement for screen readers */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {isLoading ? 'Loading conversations…' : 'Conversations loaded.'}
        </div>
        {isLoading ? (
          <>
            <ConversationListSkeleton />
            <ChatPanelSkeleton />
            <ContactPanelSkeleton />
          </>
        ) : (
          <>
            <ConversationList
              conversations={filteredConversations}
              selectedId={state.selectedConversationId}
              onSelect={handleSelectConversation}
              filter={state.filter}
              onFilterChange={handleFilterChange}
              searchQuery={state.searchQuery}
              onSearchChange={handleSearchChange}
            />

            {state.selectedConversationId !== '' && selectedContact !== null ? (
              <ChatPanel
                contact={selectedContact}
                messages={messages}
                onSendMessage={handleSendMessage}
                onSendFile={handleSendFile}
                isSendingFile={isSendingFile}
                activeChannel={state.activeChannel}
                onChannelChange={handleChannelChange}
                platform="whatsapp"
                onReaction={handleReaction}
                onDeleteMessage={handleDeleteMessage}
                onReplyMessage={handleReplyMessage}
                replyContext={replyContext}
                onCancelReply={handleCancelReply}
                onCreateLead={handleCreateLead}
              />
            ) : (
              <div
                role="region"
                aria-label="Chat area"
                className="flex flex-1 flex-col items-center justify-center text-muted-foreground"
              >
                <p>Select a conversation to start messaging</p>
              </div>
            )}

            {state.activeRightPanel === 'contact' && selectedContact !== null && (
              <ContactPanel
                contact={selectedContact}
                tags={MOCK_TAGS}
                contactIdentifier={state.selectedConversationId}
                platform="whatsapp"
              />
            )}
          </>
        )}

        <IconSidebar activeView={state.activeRightPanel} onViewChange={handleRightPanelChange} />
      </div>

      <CreateLeadDialog
        open={createLeadOpen}
        onOpenChange={open => {
          if (!open) {
            setCreateLeadOpen(false);
            setAiExtras(undefined);
            setMatchedServices(undefined);
            setUnmatchedServices(undefined);
          }
        }}
        prefillName={createLeadPrefill.name}
        prefillEmail={createLeadPrefill.email}
        prefillPhone={createLeadPrefill.phone}
        prefillSource={aiExtras?.source}
        aiExtras={aiExtras}
        matchedServices={matchedServices}
        unmatchedServices={unmatchedServices}
        orgServiceTypes={orgServiceTypes?.map((st: IOrgServiceType) => ({
          _id: st._id,
          orgId: st.orgId,
          name: st.name,
          requiredFieldCount: st.requiredFields.length,
        }))}
        onCreated={() => {
          setMatchedServices(undefined);
          setUnmatchedServices(undefined);
        }}
      />

      {isExtracting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/50">
          <div className="flex items-center gap-3 rounded-xl bg-card border border-border px-6 py-4 shadow-lg">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm font-medium text-foreground">Analyzing conversation...</span>
          </div>
        </div>
      )}
    </div>
  );
}
